import { FunctionComponent, useMemo, useState } from "react";
import {
    Box,
    Card,
    CardBody,
    CardHeader,
    Flex,
    Heading,
    Text,
    VStack,
    HStack,
    Badge,
    Avatar,
    useColorModeValue,
    Icon,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    SimpleGrid,
    Image,
    Button,
} from "@chakra-ui/react";
import { IconBuilding, IconFileText, IconChartBar, IconUsers, IconAlignBoxLeftMiddle, IconFileAnalytics } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useGetClient } from "../../../api/clients/clientQueries";
import { useGetEntitiesByClientId } from "../../../api/entities/entityQueries";
import { useGetReportsByClientId } from "../../../api/reports/reportQueries";
import { useGetTransactionsByClientId } from "../../../api/transactions/transactionQueries";
import { Entity } from "../../../api/entities/entityApi";
import { downloadClientOrgDiagramAsync } from "../../../api/clients/clientApi";
import { useEffect } from "react";

interface ClientDashboardData {
    id: string;
    name: string;
    code: string;
    leadMD: string;
    linker: string;
    description: string;
    entityCount: number;
    reportsCount: number;
    transactionsCount: number;
    jurisdictionCount: number;
    lastActivity: string;
    status: "Active" | "Inactive" | "Under Review";
    headcount: string;
}

interface Props {
    clientId?: string;
}

export const DashboardPanel: FunctionComponent<Props> = ({ clientId }) => {
    const navigate = useNavigate();

    // Fetch real data based on clientId
    const { data: client } = useGetClient(clientId);
    const { data: entities = [] } = useGetEntitiesByClientId(clientId);
    const { data: reports = [] } = useGetReportsByClientId(clientId);
    const { data: transactions = [] } = useGetTransactionsByClientId(clientId);

    // Calculate dynamic counts and data
    const entityCount = entities.length;
    const reportsCount = reports.length;
    const transactionsCount = transactions.length;

    // Get unique jurisdictions from entities
    const jurisdictionCount = new Set(entities.map((entity: Entity) => entity.operatingJurisdiction)).size;

    // Client overview data
    // Comment out org chart for now. It's not being used.
    const [orgChartPath, setOrgChartPath] = useState("");
    const hasOrgChart = orgChartPath && orgChartPath !== "";
    const clientOverview = client
        ? `${client.name} - Transfer pricing client with ${entityCount} entities across ${jurisdictionCount} jurisdictions.`
        : "Loading client information...";

    // Mock data for fields not yet available in API - this can be updated when more endpoints are available
    const clientData: ClientDashboardData = {
        id: clientId || "1",
        name: client?.name || "Loading...",
        code: client?.agressoCode, // TODO: Get from client data when available
        leadMD: client?.leadMD || "Loading...", // TODO: Get from client data when available,
        description: clientOverview,
        linker: client ? `${client.linkerFirstName} ${client.linkerLastName}` : "Loading...",
        entityCount,
        reportsCount,
        transactionsCount,
        jurisdictionCount,
        lastActivity: "2 hours ago", // TODO: Calculate from actual activity data when available
        status: "Active", // TODO: Get from client data when available
        headcount: client ? client.headcount : "Not specified",
    };

    const totalHeadcount = useMemo(() => {
        const headcountValue = client?.headcount ?? "Not specified";
        const headcountNum = parseInt(headcountValue, 10);
        return isNaN(headcountNum) ? 0 : headcountNum;
    }, [client?.headcount]);

    const cardBg = useColorModeValue("white", "gray.800");
    const borderColor = useColorModeValue("gray.200", "gray.600");
    const textColor = useColorModeValue("gray.600", "gray.300");

    const getStatusColor = (status: string) => {
        switch (status) {
            case "Active":
                return "green";
            case "Inactive":
                return "red";
            case "Under Review":
                return "yellow";
            default:
                return "gray";
        }
    };

    // Download org chart when clientId changes
    useEffect(() => {
        if (!clientId) return;
        async function downloadOrgChart() {
            try {
                const response = await downloadClientOrgDiagramAsync(clientId || "");
                const blob = await response.blob();
                setOrgChartPath(blob && blob.size > 0 ? URL.createObjectURL(blob) : "");
            } catch {
                setOrgChartPath("");
            }
        }
        downloadOrgChart();
    }, [clientId]);

    // Cleanup blob URL when orgChartPath changes
    useEffect(() => {
        return () => {
            if (orgChartPath && orgChartPath.startsWith("blob:")) {
                URL.revokeObjectURL(orgChartPath);
            }
        };
    }, [orgChartPath]);

    return (
        <VStack spacing={8} align="stretch">
            {/* Client Header */}
            <Card bg={cardBg} borderColor={borderColor}>
                <CardBody>
                    <Flex justify="space-between" align="start">
                        <HStack spacing={4} flex={1}>
                            <Avatar name={clientData.name} size="lg" bg="blue.500" color="white" />
                            <VStack align="start" spacing={1}>
                                <Heading size="lg" fontWeight={600}>
                                    {clientData.name}
                                </Heading>
                                <Text color={textColor}>
                                    Code: {clientData.code} • Lead MD: {clientData.leadMD} • Engage Linker: {clientData.linker}
                                </Text>
                                <Text fontSize="sm" color={textColor} maxW="600px">
                                    {clientData.description}
                                </Text>
                            </VStack>
                        </HStack>

                        <VStack align="end" spacing={2}>
                            <Badge colorScheme={getStatusColor(clientData.status)} variant="subtle" size="lg" px={3} py={1}>
                                {clientData.status}
                            </Badge>
                            <Text fontSize="xs" color={textColor}>
                                Last activity: {clientData.lastActivity}
                            </Text>
                        </VStack>
                    </Flex>
                </CardBody>
            </Card>

            {/* Stats Overview */}
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                <Card
                    bg={cardBg}
                    borderColor={borderColor}
                    cursor="pointer"
                    transition="all 0.2s ease-in-out"
                    _hover={{
                        transform: "translateY(-2px)",
                        shadow: "lg",
                        borderColor: "blue.300",
                        bg: useColorModeValue("blue.50", "blue.900"),
                    }}
                    w="full"
                    onClick={() => navigate(`/clients/${clientId}/entities`)}
                >
                    <CardBody>
                        <Flex align="center" justify="space-between">
                            <Stat>
                                <StatLabel fontSize="sm">Entities</StatLabel>
                                <StatNumber fontSize="2xl" color="blue.500">
                                    {clientData.entityCount}
                                </StatNumber>
                                <StatHelpText fontSize="xs">Across {clientData.jurisdictionCount} jurisdictions</StatHelpText>
                            </Stat>
                            <Icon as={IconBuilding} boxSize={8} color="blue.500" opacity={0.3} />
                        </Flex>
                    </CardBody>
                </Card>

                <Card
                    bg={cardBg}
                    borderColor={borderColor}
                    cursor="pointer"
                    transition="all 0.2s ease-in-out"
                    _hover={{
                        transform: "translateY(-2px)",
                        shadow: "lg",
                        borderColor: "purple.300",
                        bg: useColorModeValue("purple.50", "purple.900"),
                    }}
                    w="full"
                    onClick={() => navigate(`/clients/${clientId}/transactions`)}
                >
                    <CardBody>
                        <Flex align="center" justify="space-between">
                            <Stat>
                                <StatLabel fontSize="sm">Transactions</StatLabel>
                                <StatNumber fontSize="2xl" color="purple.500">
                                    {clientData.transactionsCount}
                                </StatNumber>
                                <StatHelpText fontSize="xs">Intercompany activities</StatHelpText>
                            </Stat>
                            <Icon as={IconChartBar} boxSize={8} color="purple.500" opacity={0.3} />
                        </Flex>
                    </CardBody>
                </Card>

                <Card
                    bg={cardBg}
                    borderColor={borderColor}
                    cursor="pointer"
                    transition="all 0.2s ease-in-out"
                    _hover={{
                        transform: "translateY(-2px)",
                        shadow: "lg",
                        borderColor: "green.300",
                        bg: useColorModeValue("green.50", "green.900"),
                    }}
                    w="full"
                    onClick={() => navigate(`/clients/${clientId}/reports`)}
                >
                    <CardBody>
                        <Flex align="center" justify="space-between">
                            <Stat>
                                <StatLabel fontSize="sm">Reports</StatLabel>
                                <StatNumber fontSize="2xl" color="green.500">
                                    {clientData.reportsCount}
                                </StatNumber>
                                <StatHelpText fontSize="xs">Documentation ready</StatHelpText>
                            </Stat>
                            <Icon as={IconFileText} boxSize={8} color="green.500" opacity={0.3} />
                        </Flex>
                    </CardBody>
                </Card>
            </SimpleGrid>

            {/* Client Overview Information */}
            <Card bg={cardBg} borderColor={borderColor}>
                <CardHeader>
                    <Heading size="md" fontWeight={600}>
                        Client Overview
                    </Heading>
                </CardHeader>
                <CardBody pt={0}>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                        <VStack align="start" spacing={4}>
                            <Box>
                                <HStack spacing={2} mb={2}>
                                    <Icon as={IconUsers} color="blue.500" boxSize={4} />
                                    <Text fontWeight={600} fontSize="sm" color={textColor}>
                                        Total Headcount
                                    </Text>
                                </HStack>
                                <Text fontSize="lg" fontWeight={700} color={useColorModeValue("blue.600", "blue.300")}>
                                    {totalHeadcount > 0 ? totalHeadcount.toLocaleString() : "Not specified"}
                                </Text>
                            </Box>

                            <Box>
                                <HStack spacing={2} mb={2}>
                                    <Icon as={IconAlignBoxLeftMiddle} color="teal.500" boxSize={4} />
                                    <Text fontWeight={600} fontSize="sm">
                                        Client Description
                                    </Text>
                                </HStack>
                                <Text fontSize="sm" color={textColor} lineHeight="1.5">
                                    {clientData.description}
                                </Text>
                            </Box>
                        </VStack>
                        <VStack align="start" spacing={4}>
                            <Box>
                                <HStack spacing={2} mb={2}>
                                    <Icon as={IconFileAnalytics} color="purple.500" boxSize={4} />
                                    <Text fontWeight={600} fontSize="sm">
                                        Organizational Chart
                                    </Text>
                                </HStack>
                                {hasOrgChart ? (
                                    <Box>
                                        <Image
                                            src={orgChartPath}
                                            alt="Organizational Chart"
                                            maxW="100%"
                                            maxH="200px"
                                            objectFit="contain"
                                            borderRadius="md"
                                            border="1px solid"
                                            borderColor={borderColor}
                                        />
                                        <Button size="sm" variant="outline" mt={2} onClick={() => window.open(orgChartPath, "_blank")}>
                                            View Full Size
                                        </Button>
                                    </Box>
                                ) : (
                                    <Text fontSize="sm" color={textColor}>
                                        No organizational chart uploaded
                                    </Text>
                                )}
                            </Box>
                        </VStack>
                    </SimpleGrid>
                </CardBody>
            </Card>
        </VStack>
    );
};
